import { AsyncPipe } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';

import { IdUtils } from '@ecm/consumer';
import { EcmDispatcherModule } from '@ecm/dispatcher';
import { EcmMaterialModule } from '@ecm/material';
import { ContractsBusinessModel, PortfolioService } from '@ecm/portfolio';
import { TitleService } from '@ecm/router';
import { KenobiParams, Status } from '@ecm/tracker';

import { Dashboard } from '../../../../business/api/dashboard/dashboard';
import { Family } from '../../../../business/api/family/family';
import { DashboardReco } from '../../../../business/api/reco/dashboardReco';
import { CrossroadsViewModelMapper } from '../../../../business/mapper/crossroads/crossroads.view-model-mapper';
import { ActiveContractViewModel } from '../../../../business/model/crossroads/contracts-options/active-contract.view-model';
import { CrossroadsViewModel } from '../../../../business/model/crossroads/crossroads.view-model';
import { KenobiCodes } from '../../../../business/model/tracking/kenobiCodes';
import { ContractRecoService } from '../../../../services/contractReco.service';
import { DashboardService } from '../../../../services/dashboard.service';
import { FamilyService } from '../../../../services/family.service';
import { TrackingService } from '../../../../services/tracking.service';
import { AbstractParentComponent } from '../../../shared/abstract-parent.component';
import { CrossroadsUtils } from '../../utils/crossroads.utils';
import { ActiveContractComponent } from '../active-contract/active-contract.component';
import { ContractSuggestedComponent } from '../contract-suggested/contract-suggested.component';
import { MultiContractItemComponent } from '../multi-contract-item/multi-contract-item.component';
import { OtherContractsComponent } from '../other-contract/other-contracts.component';
import { ProtectedHouseContractComponent } from '../protected-house-contract/protected-house-contract.component';
import { TerminatedContractComponent } from '../terminated-contract/terminated-contract.component';

@Component({
    selector: 'contracts-and-options',
    templateUrl: 'contracts-and-options.html',
    styleUrls: ['contracts-and-options.css'],
    standalone: true,
    imports: [
        ContractSuggestedComponent,
        EcmMaterialModule,
        ActiveContractComponent,
        ProtectedHouseContractComponent,
        TerminatedContractComponent,
        MultiContractItemComponent,
        OtherContractsComponent,
        AsyncPipe,
        TranslateModule,
    ],
})
export class ContractsAndOptionsComponent extends AbstractParentComponent implements OnInit {
    /**
     * Transaction properties for BCO monitoring
     */
    private readonly pageId: string;

    public viewModel: CrossroadsViewModel;

    public activeContractViewModel: ActiveContractViewModel;

    private readonly trackingService = inject(TrackingService);
    private readonly dashboardService = inject(DashboardService);
    private readonly familyService = inject(FamilyService);
    private readonly contractRecoService = inject(ContractRecoService);
    private readonly titleService = inject(TitleService);
    private readonly portfolioService = inject(PortfolioService);
    private readonly translate = inject(TranslateService);

    public constructor() {
        super();
        this.translate.use('fr');
        this.pageId = IdUtils.generateRandomId();
    }

    public ngOnInit(): void {
        super.ngOnInit();
        EcmDispatcherModule.sendSurveyParams({ section: 'NEC', idPage: 'gt-home-page' });

        this.translate.get('dashboard.contractsAndOptions').subscribe((contractsAndOptionTranslations: any) => {
            this.titleService.setTitle(contractsAndOptionTranslations['title']);
            EcmDispatcherModule.sendBackRoute('accueil');
            this.dashboardService.getDashboard(KenobiCodes.CTROPT, this.pageId).subscribe((dashboard: Dashboard) => {
                this.portfolioService.getRawContracts().subscribe((contracts: ContractsBusinessModel) => {
                    this.familyService
                        .isFamilyEligible(KenobiCodes.CTROPT, this.pageId)
                        .subscribe((isFamilyEligible: boolean) => {
                            if (isFamilyEligible) {
                                this.familyService
                                    .getFamily(KenobiCodes.CTROPT, this.pageId)
                                    .subscribe((family: Family) => {
                                        this.getCrossroads(
                                            dashboard,
                                            contracts,
                                            contractsAndOptionTranslations,
                                            isFamilyEligible,
                                            family,
                                        );
                                    });
                            } else {
                                this.getCrossroads(
                                    dashboard,
                                    contracts,
                                    contractsAndOptionTranslations,
                                    isFamilyEligible,
                                );
                            }
                        });
                });
            });
        });
    }

    /**
     * suggestedContract title in i18n according to 1 or more contrats
     * @return {Observable<string>}
     */
    public getContractTitle(): Observable<string> {
        return this.translate.get(
            'dashboard.contractsAndOptions'.concat(
                this.viewModel.contracts.length > 1 ? '.subTitleMultiContracts' : '.subTitleSingleContract',
            ),
        );
    }

    /**
     * get suggestedContract title in i18n according to 1 or more contrats
     * @return {Observable<string>}
     */
    public getSuggestedContractTitle(): Observable<string> {
        return this.translate.get(
            'dashboard.contractsAndOptions.suggestedContract.'.concat(
                this.viewModel.suggestedContracts.length > 1 ? 'titleMultiContracts' : 'titleSingleContract',
            ),
        );
    }

    /**
     * get suggestedContract subTitle in i18n according to 1 or more contrats
     * @return {Observable<string>}
     */
    public getSuggestedContractSubTitle(): Observable<string> {
        return this.translate.get(
            'dashboard.contractsAndOptions.suggestedContract.'.concat(
                this.viewModel.suggestedContracts.length > 1 ? 'subTitleMultiContracts' : 'subTitleSingleContract',
            ),
        );
    }

    /**
     * Check if sosh and call get crossRoadsViewModel
     * @param {Dashboard} dashboard
     * @param {ContractsBusinessModel} contracts
     * @param {any} contractsAndOptionTranslations
     * @param {boolean} isFamilyEligible
     * @param {Family} family (optional)
     */
    private getCrossroads(
        dashboard: Dashboard,
        contracts: ContractsBusinessModel,
        contractsAndOptionTranslations: any,
        isFamilyEligible: boolean,
        family?: Family,
    ) {
        const isSosh = this.isSoshBrand()();
        if (!isSosh) {
            this.contractRecoService
                .getContractReco(KenobiCodes.CTROPT, this.pageId)
                .subscribe((contractsReco: DashboardReco[]) => {
                    if (contractsReco) {
                        this.viewModel = CrossroadsViewModelMapper.getCrossroadsViewModel(
                            dashboard,
                            contracts,
                            contractsAndOptionTranslations,
                            isFamilyEligible,
                            family,
                            contractsReco,
                        );
                        CrossroadsUtils.orderModel(this.viewModel, isSosh);
                        this.trackView();
                    }
                });
        } else {
            CrossroadsUtils.orderModel(
                (this.viewModel = CrossroadsViewModelMapper.getCrossroadsViewModel(
                    dashboard,
                    contracts,
                    contractsAndOptionTranslations,
                    isFamilyEligible,
                    family,
                )),
                true,
            );
            this.trackView();
        }
    }

    /**
     * Tracks view in kenobi and TMS
     */
    private trackView(): void {
        this.trackingService.trackContractOptionsView();
        const kenobiParams: KenobiParams = new KenobiParams(
            KenobiCodes.CTROPT['functionality'],
            KenobiCodes.CTROPT['rubric'],
            Status.OK,
        );
        kenobiParams.comment = this.viewModel.suggestedContracts.length.toString();
        this.trackingService.trackKenobi(kenobiParams, this.pageId);
    }
}
